import React from 'react';
import { isSameDate as defaultIsSameDate, getSafeTime as defaultGetSafeTime } from '../lib/helpers';

// Helper to parse ordered_by and message from order notes
const defaultParseOrderNote = (note) => {
  if (!note) return { orderedBy: 'ไม่ระบุ', noteText: '' };
  if (typeof note === 'string' && note.startsWith("ordered_by:")) {
    const pipeIdx = note.indexOf("|note:");
    if (pipeIdx !== -1) {
      const orderedBy = note.substring(11, pipeIdx);
      const noteText = note.substring(pipeIdx + 6);
      return { orderedBy, noteText };
    }
  }
  return { orderedBy: 'ไม่ระบุ', noteText: note };
};

// Helper to parse database image_url column into full and zoom URLs
const defaultParseImages = (imageUrlField) => {
  if (!imageUrlField) return { full: '', zoom: '', other: '', document: '' };
  if (typeof imageUrlField !== 'string') return { full: '', zoom: '', other: '', document: '' };

  if (imageUrlField.startsWith('{') && imageUrlField.endsWith('}')) {
    try {
      const parsed = JSON.parse(imageUrlField);
      return {
        full: parsed.full || '',
        zoom: parsed.zoom || '',
        other: parsed.other || '',
        document: parsed.document || ''
      };
    } catch (e) {
      return { full: imageUrlField, zoom: '', other: '', document: '' };
    }
  }
  return { full: imageUrlField, zoom: '', other: '', document: '' };
};

const DispensePage = ({
  scanErrorMessage = '',
  setScanErrorMessage,
  selectedOrder = null,
  setSelectedOrder,
  sterileSuccessList = [],
  setSterileSuccessList,
  itemMaster = [],
  showPreview = {},
  setShowPreview,
  handleTogglePreview,
  parseImages = defaultParseImages,
  barcodeInputRef,
  handleBlurToFocus,
  barcodeInput = '',
  setBarcodeInput,
  handleDispenseScan,
  setCameraTargetField,
  setIsCameraOpen,
  // eslint-disable-next-line no-unused-vars
  targetWarehouse = '',
  setTargetWarehouse,
  // eslint-disable-next-line no-unused-vars
  WAREHOUSES_LIST = [],
  handleConfirmDispense,
  handleConfirmSterileBasket,
  handleCancelOrder,
  getUserNameByEmail,
  orders = [],
  dispenseHistoryDate = '',
  setDispenseHistoryDate,
  isSameDate = defaultIsSameDate,
  getSafeTime = defaultGetSafeTime,
  handlePrintBill,
  parseOrderNote = defaultParseOrderNote,
  // eslint-disable-next-line no-unused-vars
  instruments = [],
  // eslint-disable-next-line no-unused-vars
  instrumentTags = []
}) => {
  const resolveUserName = getUserNameByEmail || ((email) => email || 'ไม่ระบุ');
  const onConfirm = handleConfirmDispense || handleConfirmSterileBasket;
  const checkSameDate = isSameDate || defaultIsSameDate;
  const checkSafeTime = getSafeTime || defaultGetSafeTime;
  const parseNote = parseOrderNote || defaultParseOrderNote;
  const parseImgs = parseImages || defaultParseImages;

  const resolveImageUrl = (url) => {
    if (!url) return '';
    const parsed = parseImgs(url);
    return parsed.zoom || parsed.full || url;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {scanErrorMessage && (
        <div style={{ background: '#ff4d4d', color: '#fff', padding: '15px', borderRadius: '10px', textAlign: 'center', fontWeight: 'bold' }}>
          ⚠️ {scanErrorMessage}{' '}
          <button onClick={() => setScanErrorMessage && setScanErrorMessage('')} style={{ marginLeft: '10px', cursor: 'pointer' }}>
            ปิด
          </button>
        </div>
      )}

      <div style={{ background: '#fff', padding: '25px', borderRadius: '15px', border: selectedOrder ? '3px solid #e65100' : '1px solid #ddd' }}>
        <h3 style={{ color: '#e65100', marginTop: 0 }}>📦 Order ที่กำลังจัดจ่าย</h3>
        {selectedOrder ? (
          <>
            <div style={{ marginBottom: '15px', padding: '15px', background: '#fff3e0', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <strong style={{ fontSize: '1.2em' }}>📍 สั่งจาก: {selectedOrder.clinic_name}</strong>
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                  ⏰ เวลา: {new Date(selectedOrder.timestamp).toLocaleString('th-TH')}
                </div>
                {selectedOrder.note && (
                  <div style={{ fontSize: '0.9em', color: '#d35400', marginTop: '8px', fontWeight: 'bold' }}>
                    📝 ข้อความ: {selectedOrder.note}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
              {selectedOrder.items.map((it, idx) => {
                const scannedQty = sterileSuccessList.filter(s => s.item_name === it.name).length;
                const isDone = scannedQty >= it.qty;

                const masterItem = itemMaster.find(masterLot => masterLot.item_name === it.name);
                const locText = [];
                if (masterItem?.cabinet) locText.push(`ตู้: ${masterItem.cabinet}`);
                if (masterItem?.shelf) locText.push(`ชั้น: ${masterItem.shelf}`);
                if (masterItem?.row) locText.push(`แถว: ${masterItem.row}`);
                const locationDisplay = locText.length > 0 ? locText.join(' ') : 'ไม่ระบุพิกัด';

                return (
                  <div key={idx} style={{ padding: '15px', borderRadius: '10px', background: isDone ? '#27ae60' : '#f8f9fa', color: isDone ? '#fff' : '#333', border: '1px solid #ddd' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: '1.1em' }}>{it.name}</strong>
                      {masterItem?.image_url && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (handleTogglePreview) handleTogglePreview(`dispense_${it.name}`);
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em' }}
                        >
                          🖼️
                        </button>
                      )}
                    </div>

                    {showPreview[`dispense_${it.name}`] && masterItem?.image_url && (
                      <div style={{ marginTop: '5px', textAlign: 'center' }}>
                        <img
                          src={resolveImageUrl(masterItem.image_url)}
                          alt={it.name}
                          style={{ maxWidth: '100px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                      </div>
                    )}

                    <div style={{ fontSize: '0.85em', marginTop: '5px', marginBottom: '8px', color: isDone ? '#e8f5e9' : '#7f8c8d' }}>
                      📍 {locationDisplay}
                    </div>

                    <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>({scannedQty}/{it.qty})</span> {isDone && '✓'}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p style={{ textAlign: 'center', color: '#999' }}>กรุณาเลือก Order ด้านล่าง</p>
        )}
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <input
          type="text"
          ref={barcodeInputRef}
          onBlur={handleBlurToFocus}
          value={barcodeInput}
          onChange={(e) => {
            const val = e.target.value;
            if (setBarcodeInput) setBarcodeInput(val);
            if (val.length === 13 || val.length === 14) {
              if (handleDispenseScan) handleDispenseScan(val);
              if (setBarcodeInput) setBarcodeInput('');
            }
          }}
          placeholder="สแกนรหัส 13 หรือ 14 หลัก... (หรือรหัสกล่อง)"
          style={{ flex: 1, padding: '20px', fontSize: '1.5em', border: '3px solid #fbbf24', borderRadius: '10px', textAlign: 'center' }}
          autoFocus
        />
        <button
          onClick={() => {
            if (setCameraTargetField) setCameraTargetField('main');
            if (setIsCameraOpen) setIsCameraOpen(true);
          }}
          style={{ padding: '0 25px', background: '#fbbf24', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '2em' }}
        >
          📷
        </button>
      </div>

      <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
        <button
          onClick={onConfirm}
          disabled={sterileSuccessList.length === 0 || !selectedOrder}
          style={{
            flex: 3,
            padding: '20px',
            background: (sterileSuccessList.length === 0 || !selectedOrder) ? '#ccc' : '#e65100',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1.4em',
            fontWeight: 'bold',
            cursor: (sterileSuccessList.length === 0 || !selectedOrder) ? 'not-allowed' : 'pointer'
          }}
        >
          ยืนยันจ่ายของเข้า {selectedOrder?.clinic_name || '...'}
        </button>

        <button
          onClick={() => {
            if (setSelectedOrder) setSelectedOrder(null);
            if (setSterileSuccessList) setSterileSuccessList([]);
            if (setTargetWarehouse) setTargetWarehouse('');
            if (setShowPreview) setShowPreview({});
          }}
          disabled={!selectedOrder}
          style={{
            flex: 1,
            padding: '20px',
            background: !selectedOrder ? '#ccc' : '#7f8c8d',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1.2em',
            fontWeight: 'bold',
            cursor: !selectedOrder ? 'not-allowed' : 'pointer'
          }}
        >
          ↩️ ยกเลิกการจัดชุดนี้
        </button>
      </div>

      <div style={{ background: '#fff', padding: '25px', borderRadius: '15px' }}>
        <h3>📝 รายการ Order รอจัดจ่าย</h3>
        {orders.filter(o => o.status === 'pending').sort((a, b) => checkSafeTime(a.timestamp) - checkSafeTime(b.timestamp)).map(order => (
          <div key={order.id} style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '10px', marginBottom: '10px', background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
              <div>
                <strong>📍 {order.clinic_name}</strong>
                <div style={{ fontSize: '0.8em', color: '#666', marginTop: '3px' }}>⏰ {new Date(order.timestamp).toLocaleString('th-TH')}</div>
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={() => handleCancelOrder && handleCancelOrder(order.id, order.clinic_name)}
                  style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  ❌ ยกเลิก Order
                </button>
                <button
                  onClick={() => {
                    if (setSelectedOrder) setSelectedOrder(order);
                    if (setTargetWarehouse) setTargetWarehouse(order.clinic_name);
                    if (setSterileSuccessList) setSterileSuccessList([]);
                    if (setShowPreview) setShowPreview({});
                  }}
                  style={{ background: '#e65100', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1.1em' }}
                >
                  📦 เลือกจัดของ
                </button>
              </div>
            </div>
            {(() => {
              const parsed = parseNote(order.note);
              return (
                <div style={{ fontSize: '0.9em', color: '#d35400', marginBottom: '8px', background: '#fff3e0', padding: '8px', borderRadius: '5px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <div>👤 ผู้สั่ง: <strong style={{ color: '#333' }}>{resolveUserName(parsed.orderedBy)}</strong></div>
                  {parsed.noteText && <div>📝 ข้อความ: {parsed.noteText}</div>}
                </div>
              );
            })()}
            {order.items.map((it, i) => <div key={i} style={{ fontSize: '0.9em' }}>- {it.name} ({it.qty})</div>)}
          </div>
        ))}
        {orders.filter(o => o.status === 'pending').length === 0 && <p style={{ textAlign: 'center', color: '#999' }}>ไม่มี Order ค้างจ่าย</p>}
      </div>

      <div style={{ background: '#fff', padding: '25px', borderRadius: '15px' }}>
        <h3>
          🗂️ ประวัติการจ่ายของ (สำเร็จ / ยกเลิก)
          <input
            type="date"
            value={dispenseHistoryDate}
            onChange={(e) => setDispenseHistoryDate && setDispenseHistoryDate(e.target.value)}
            style={{ marginLeft: '10px', padding: '5px', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.7em' }}
          />
        </h3>
        {orders
          .filter(o => (o.status === 'completed' || o.status === 'cancelled') && checkSameDate(o.timestamp, dispenseHistoryDate))
          .sort((a, b) => checkSafeTime(b.timestamp) - checkSafeTime(a.timestamp))
          .map(order => {
            const isCompleted = order.status === 'completed';
            const isCancelled = order.status === 'cancelled';
            return (
              <div
                key={order.id}
                style={{
                  border: isCompleted ? '1px solid #a5d6a7' : '1px solid #ef9a9a',
                  padding: '15px',
                  borderRadius: '10px',
                  marginBottom: '10px',
                  background: isCompleted ? '#f1f8e9' : '#ffebee'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>📍 {order.clinic_name}</strong>
                    {handlePrintBill && isCompleted && (
                      <button
                        onClick={() => handlePrintBill(order)}
                        style={{ background: '#3498db', color: '#fff', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8em' }}
                      >
                        🖨️ พิมพ์ใบ
                      </button>
                    )}
                  </div>
                  {isCompleted ? (
                    <span style={{ color: '#2e7d32', fontWeight: 'bold', textAlign: 'right' }}>
                      ✅ สำเร็จ {order.cancel_reason && <span style={{ color: '#d35400', display: 'block', fontSize: '0.85em' }}>(⚠️ {order.cancel_reason})</span>}
                    </span>
                  ) : (
                    <span style={{ color: '#c62828', fontWeight: 'bold', textAlign: 'right' }}>
                      ❌ {order.cancel_reason}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '5px' }}>⏰ {new Date(order.timestamp).toLocaleString('th-TH')}</div>
                {(() => {
                  const parsed = parseNote(order.note);
                  return (
                    <div style={{ fontSize: '0.85em', color: '#d35400', marginBottom: '5px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <div>👤 ผู้สั่ง: <strong style={{ color: '#333' }}>{resolveUserName(parsed.orderedBy)}</strong></div>
                      {parsed.noteText && <div>📝 ข้อความ: {parsed.noteText}</div>}
                    </div>
                  );
                })()}

                <div style={{ marginTop: '10px' }}>
                  {order.items.map((it, i) => {
                    const dispensedQty = it.dispensed_qty !== undefined ? it.dispensed_qty : it.qty;
                    const isFulfilled = dispensedQty >= it.qty;
                    return (
                      <div
                        key={i}
                        style={{
                          fontSize: '0.85em',
                          color: '#666',
                          textDecoration: isCancelled ? 'line-through' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}
                      >
                        {isCompleted ? (isFulfilled ? '✅' : '❌') : '-'}
                        <span>
                          {it.name}
                          {isCompleted && !isFulfilled ? ` (สั่ง ${it.qty}, ได้ ${dispensedQty})` : ` (${it.qty})`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        {orders.filter(o => (o.status === 'completed' || o.status === 'cancelled') && checkSameDate(o.timestamp, dispenseHistoryDate)).length === 0 && (
          <p style={{ textAlign: 'center', color: '#999' }}>ไม่มีประวัติการจ่ายของในวันที่เลือก</p>
        )}
      </div>
    </div>
  );
};

export default DispensePage;
